import React from 'react';
import { Check, X, Zap, Target, RefreshCw, Shield } from 'lucide-react';

export const Differentiation = () => {
  const capabilities = [
    {
      icon: Target,
      title: 'We Don\'t Report. We Prove.',
      description: "Every finding comes with a screen recording of us breaking in. Try getting that from a scanner.",
    },
    {
      icon: Zap,
      title: 'Pentest Depth. Push-to-Deploy Speed.',
      description: 'What takes a human team 3 weeks, we do in minutes. On every single build. Not once a year.',
    },
    {
      icon: RefreshCw,
      title: 'Security That Ships When You Ship.',
      description: 'You deploy on Friday. We test on Friday. Not next quarter. Not when your pentest vendor has availability.',
    },
    {
      icon: Shield,
      title: 'Your Team Does Strategy. We Do Everything Else.',
      description: 'Stop burning your best engineers on repetitive security testing. Let them think. Let us find.',
    },
  ];

  const comparison = [
    { feature: 'Finds Business Logic Flaws', cozmoz: true, scanners: false, pentests: true },
    { feature: 'Tests Every Build Automatically', cozmoz: true, scanners: true, pentests: false },
    { feature: 'Chains Multi-Step Exploits', cozmoz: true, scanners: false, pentests: true },
    { feature: 'Video Proof of Every Exploit', cozmoz: true, scanners: false, pentests: false },
    { feature: 'Lives in Your CI/CD Pipeline', cozmoz: true, scanners: true, pentests: false },
    { feature: 'Doesn\'t Cost $50K Per Engagement', cozmoz: true, scanners: true, pentests: false },
  ];

  return (
    <section className="relative">
      <div className="section-divider"></div>

      <div className="container mx-auto px-6 py-28">
        <div className="max-w-5xl mx-auto">
          {/* Section header */}
          <div className="mb-20">
            <div className="tag mb-8">
              <span className="w-[6px] h-[6px] rounded-full bg-[#00ffd5] shadow-[0_0_8px_#00ffd5]"></span>
              Capabilities
            </div>
            <h2 className="text-4xl md:text-6xl font-extrabold text-white mb-6 tracking-tight">
              Scanners Check Boxes.
              <br />
              <span className="gradient-accent">We Break In.</span>
            </h2>
            <p className="text-lg text-white/30 max-w-2xl" style={{ fontFamily: 'var(--font-body)' }}>
              There's a reason the best hackers laugh at automated scanners.
              We built what they'd actually be afraid of.
            </p>
          </div>

          {/* Capabilities grid */}
          <div className="grid md:grid-cols-2 gap-4 mb-20">
            {capabilities.map((cap, index) => (
              <div
                key={index}
                className="card-tactical rounded-sm p-6 group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 bg-[#00ffd5]/[0.06] border border-[#00ffd5]/15">
                    <cap.icon className="w-4 h-4 text-[#00ffd5]/70" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white mb-1">
                      {cap.title}
                    </h3>
                    <p className="text-white/30 text-sm leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                      {cap.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Comparison table - terminal style */}
          <div className="bg-[#0d0d12] border border-white/[0.06] rounded-sm overflow-hidden">
            {/* Table header */}
            <div className="flex items-center gap-2 px-6 py-3 border-b border-white/[0.04] bg-white/[0.01]">
              <span className="font-mono text-[0.65rem] text-white/40 tracking-wider">COMPARISON_MATRIX.TSV</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left py-4 px-6 font-mono text-[0.7rem] text-white/25 tracking-wider uppercase">
                      Capability
                    </th>
                    <th className="text-center py-4 px-4 font-mono text-[0.7rem] tracking-wider">
                      <span className="text-[#00ffd5]">COZMOZ AI</span>
                    </th>
                    <th className="text-center py-4 px-4 font-mono text-[0.7rem] text-white/25 tracking-wider">
                      SCANNERS
                    </th>
                    <th className="text-center py-4 px-4 font-mono text-[0.7rem] text-white/25 tracking-wider">
                      PENTESTS
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, index) => (
                    <tr key={index} className="border-b border-white/[0.03] hover:bg-white/[0.01] transition-colors">
                      <td className="py-3.5 px-6 text-sm text-white/40" style={{ fontFamily: 'var(--font-body)' }}>
                        {row.feature}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        {row.cozmoz ? (
                          <Check className="w-4 h-4 text-[#00ffd5] mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-[#ff3333]/40 mx-auto" />
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        {row.scanners ? (
                          <Check className="w-4 h-4 text-white/30 mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-[#ff3333]/20 mx-auto" />
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        {row.pentests ? (
                          <Check className="w-4 h-4 text-white/30 mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-[#ff3333]/20 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Differentiation;
